import React from 'react';
import Relay from 'react-relay';

import DateField from './DateField';
import TextField from './TextField';
import Select from 'react-select';
import Dialog from 'react-bootstrap-dialog';
import Config from '../Config';
import Tabs from './Tabs'
import SportunityTable from './SportunityTable'
import ChatTable from './ChatTable'
import UserList from './UserList'
import Button from './Button';
import UserNavigationSessions from './UserNavigationSessions';

import ValidateUserMutation from '../mutations/User/ValidateUserMutation';
import MailValidationMutation from '../mutations/User/MailValidationMutation';
import UpdateUserMutation from '../mutations/User/UpdateUserMutation';

export const getOptionsFromUsers = (users) => users && users.edges.length && users.edges.map((edge) => ({
  value: edge.node.id, label: edge.node.pseudo, user: edge.node,
}))

const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const isValidEmailAddress = (address) => emailRegex.test(address)

const ADMIN_LEVEL = 'ADMIN'

const pipe = (fns) => (arg) => fns.reduce((arg, nextF) => nextF(arg), arg)

const projection = (config) => (target) =>
  Object
    .keys(config)
    .reduce((mem, field) => ({
      ...mem,
      [field]: typeof config[field] === "function" ? config[field](target) : target[field],
    }), {})

const appendAuthorized = (authorized_managers, user) => [
  ...(authorized_managers.map(projection({authorization_level: true, user: (t) => t.user.id}))),
  { authorization_level: ADMIN_LEVEL, user: user.id }
]

const notThisUser = (user) => (options) =>
  (options || []).filter((option) => user.id !== option.user.id)

const notThoseUsers = (users) => (options) =>
  users.reduce((options, user) => notThisUser(user)(options), options)

const getOptionsFromUsersWithoutThisUser = (user) => pipe([
  getOptionsFromUsers,
  notThoseUsers([user, user.authorized_managers.map((am) => am.user)]),
])

const tabsSportunitiesOptionsDict = {
  Organized: 'Organized',
  Booked: 'Booked',
  Invited: 'Invited',
  Declined: 'Declined',
  Passed: 'Past',
}

const redirection = ({ edit, router }) =>
  (sportunity, index, event) => {
    event.stopPropagation()
    router.push({
      pathname : '/sportunities/edit/'+sportunity.id,
      state :{
        edit,
      },
    })
  }

const tabsSportunitiesOptions = ({ viewer, relay, router }, { sportunitiesLoading }, setState) =>
  Object.keys(tabsSportunitiesOptionsDict).map((item, index) => ({
    head: () => `${item} Sportunities`,
    content: (props) => viewer.sportunities && (
      <SportunityTable
        dataSource={!sportunitiesLoading && viewer.sportunities.edges}
        filters={{}}
        onItemClick={redirection({ edit: false, router })}
        onEditItemClick={redirection({ edit: true, router })}
      />
    ),
    onSelect: () => {
      setState({
        sportunitiesLoading: true,
      })
      relay.setVariables({
        fetchSportunities: true,
        filter: { status: tabsSportunitiesOptionsDict[item] }
      }, ({ done, aborted }) => (done || aborted) && setState({
        sportunitiesLoading: false,
      }))
    }
  }))

const tabsChatsOptionsDict = {
  'One to One': ({ node: chat }) => (!chat.sportunity && chat.users.length <= 2),
  Group: ({ node: chat }) => chat.sportunity || chat.users.length > 2,
}

const onEditChatClick = ({ user, router }) => (item, index) => {
  if(item == user){
    return;
  }

  let path = '/chats/edit/'+item.id;
  router.push({
    pathname : path,
    state :{
      chatId:item.id,
    }
  })
}

const loadMoreFromRelay = (relay, variables, cb) => {
  cb(false)
  relay.setVariables(variables, ({ done, aborted }) => cb(done || aborted))
}

const LoadMore = ({ chats, relay, onReady }) =>
  (chats.pageInfo.hasNextPage || chats.pageInfo.hasPreviousPage)
  ? (
        <div style={{
          justifyContent: 'space-around',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Button
            onClick={() =>
              loadMoreFromRelay(relay, {
                chatsSize: relay.variables.chatsSize*2
              }, onReady)
            }
            style={styles.close}
            text={(
              <span>
                <i style={styles.closeIcon} className="glyphicon glyphicon-plus-sign glyphicon-2x" />
                Load more
              </span>
            )}
          />
        </div>
    )
  : null

const tabsChatsOptions = ({ user, relay, router, onReady }, { chatsLoading }, setState) =>
  Object.keys(tabsChatsOptionsDict).map((item, index) => ({
    head: () => `${item} Chats`,
    content: (props) => user.chats && (
      <div>
        <ChatTable
          dataSource={!chatsLoading && user.chats.edges.filter(tabsChatsOptionsDict[item])}
          filters={{}}
          onItemClick={onEditChatClick({ user, router })}
        />
        {!chatsLoading && (
          <LoadMore
            chats={user.chats}
            relay={relay}
            onReady={(done) => setState({
              chatsLoading: !done,
            })}
          />
        )}
        {chatsLoading && (
          <div>Loading...</div>
        )}
      </div>
    ),
    onSelect: () => {
      setState({
        chatsLoading: true,
      })
      relay.setVariables({
        fetchChats: true,
      }, ({ done, aborted }) => (done || aborted) && setState({
        chatsLoading: false,
      }))
    }
  }))

const hasEndingSlash = (s) => s[s.length-1] === '/'
const withEndingSlash = (s) => hasEndingSlash(s)? s : s + '/'


class UserPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentUser: props.user,
      autocompleteUsersQuery: '',
      autocompleteUserSelected: null,
      seeNavigationSessions: false,
    }

    props.relay.setVariables({
      userId: props.user.id,
    })

    this.getOptionsFromUsers = getOptionsFromUsersWithoutThisUser(props.user)
  }

  _handleSearchInputChange = (inputContent) => {
    const isEmail = isValidEmailAddress(query)
    const field = isEmail? 'Email' : 'Pseudo'
    this.setState({
      autocompleteEmail: '',
      autocompletePseudo: '',
      ['autocomplete'+field]: inputContent,
      autocompleteUsersQuery: inputContent,
      autocompleteUserSelected: null,
    })
  }

  componentDidUpdate(prevProps, prevState){

    let user = this.state.currentUser;
    this.refs.status = this._getStatus(user);
  }

  onSaveClick() {
    let user = this.state.currentUser;

    if (this._validUser(user))
       this.props.onUpdateUser(user);
    else
      alert('invalid user');

  }

  onValidatePublicActivitiesClick(){
    let user = this.state.currentUser;

    if (this._validUser(user)){
      user.isTrustedUserToCreateActivities = true;
      this.props.onUpdateUser(user);
    }else
      alert('invalid user');
  }

  onValidatePublicGroupsClick(){
    let user = this.state.currentUser;

    if (this._validUser(user)){
      user.isTrustedUserToCreateCircles = true;
      this.props.onUpdateUser(user);
    }else
      alert('invalid user');
  }

  accessAsThisUser = () => {
    const { token } = this.state.currentUser
    const url = `${withEndingSlash(Config.webapp || '')}login/${token}`
    window.open(url, '_blank')
  }

  seeThisUserNavigationSessions = () => {
    this.setState({seeNavigationSessions: true})
  }
  closeNavigationSessions = () => {
    this.setState({seeNavigationSessions: false})
  }

  _validUser(user) {
    return true;
  }

  _getStatus(user) {
    let status = 'ACTIVE';
    if (user && user.creation_status && user.creation_status.status)
      status = user.creation_status.status;

    return status ;
  }

  _handleStatusChange(event, user) {
    user.creation_status.status = event.target.value;
    this.setState({
      currentUser: user
    });
  }

  _handleTransferTrustedChange(event, user) {
    user.isTrustedUserToMakeMoneyTransferToAnyone = event.target.checked ? true : false ;
    this.setState({
      currentUser: user
    })
  }

  _handleUserSearchInput = (inputContent) => {
    clearTimeout(this._handleUserSearchInput.timeout)
    const fireIt = () => this._handleAutocompleteUserQueryChange(inputContent)
    this._handleUserSearchInput.timeout = setTimeout(fireIt, 400)
  }

  _handleAuthorizedManagersSave = (users) => {
    const { currentUser: user } = this.state
    const { viewer } = this.props
    this.props.relay.commitUpdate(
      new UpdateUserMutation({
        viewer,
        user,
        authorized_managers: users.map((u) => ({
          authorization_level: ADMIN_LEVEL,
          user: u.id
        })),
      }), {
        onSuccess: () => {
          this.dialog.showAlert(`Authorized managers was successful updated!`)
        },
        onFailure: (error) => {
          console.error(error)
        }
      }
    )
  }

  _handleFeesChange(event, user) {
    user.fees = event.target.value;
    this.setState({
      currentUser: user
    });
  }

  _handleReasonChange(event, user) {
    user.creation_status.reason = event.target.value;
    this.setState({
      currentUser: user
    });
  }

  _handleCertificateStatusChange(event, sportIndex, certificateIndex) {
    let user = this.state.currentUser;

    user.sports[sportIndex].certificates[certificateIndex].validation = event.target.value;

    this.setState({
      currentUser: user
    });
  }

  _renderAvatarArea(avatar) {
    return (
      <div style={{display:'flex', flexDirection:'column'}}>
          <img
            src={avatar}
            style={{width:150, height:150}}/>
      </div>
    )
  }

  _renderStatusField(user) {
    let status = this._getStatus(user);
    let userStatusList = this.props.userStatusList;

    return (
      <div style={{display: 'flex', flexDirection: 'column', marginTop: 20, marginRight: 10}}>
        <label>Status</label>
        <select
          value={status}
          onChange={(e) => this._handleStatusChange(e, user)}
          className="form-control"
          ref="status">
          {
            userStatusList.map((item, index) => {
              return (<option key={index}>{item.name}</option>)
            })
          }
        </select>
      </div>
    );
  }

  _renderCertificateField(certificate, sportIndex, certificateIndex) {
    let certificateValidationStatusList = this.props.certificateValidationStatusList;

    return (
      <div style={{display: 'flex', flexDirection: 'column', marginTop: 20, marginRight: 10}}>
        <label>{certificate.certificate.name.EN} : </label>
        <select
          value={certificate.validation}
          onChange={(e) => this._handleCertificateStatusChange(e, sportIndex, certificateIndex)}
          className="form-control"
          ref="status">
          {
            certificateValidationStatusList.map((item, index) => {
              return (<option key={index}>{item.name}</option>)
            })
          }
        </select>
      </div>
    );
  }

  _renderCertificatesField(certificates, sportIndex) {
    return (
      <div style={{paddingBottom:5, paddingTop:7, paddingLeft:20}}>
        <label>Certificates :</label>
        <div>
          {(certificates.length > 0) ? certificates.map((certificate,index) => {return (<div key={index}>{this._renderCertificateField(certificate, sportIndex,  index)}</div>)} ) : "None"}
        </div>
      </div>
    )
  }

  renderOption = (option) => (
		<div style={{ display: 'flex', flexDirection: 'row'}}>
      <img src={option.user.avatar} style={{ height: 20, width: 'auto', padding: 4, verticalAlign: 'middle' }} />
      {option.label}
    </div>
	)

  _handleSportunitiesLoading = () => this.setState({
    sportunitiesLoading: false,
  })

  _handleForceValidateUser = () => {
    this.dialog.show({
      title: `Warning`,
      body: `Want validate this user's email? PS: This action cannot be undo.`,
      actions: [
        Dialog.CancelAction(),
        Dialog.OKAction(() => {
          this._forceValidateUser()
        })
      ],
      bsSize: 'small',
    })
  }

  _forceValidateUser() {
    const { token } = this.state.currentUser
    const { viewer } = this.props
    this.props.relay.commitUpdate(
      new MailValidationMutation({
        viewer,
        token,
      }), {
        onSuccess: () => {
          this.dialog.showAlert(`User's email validation was successful updated!`)
        },
        onFailure: (error) => {
          console.error(error)
        }
      }
    )
  }

  render() {
    const user = this.state.currentUser;

    if (this.state.seeNavigationSessions)
      return (
        <div>
          <div style={{paddingLeft:20, marginTop:25}}>
            <button type="button" className="btn btn-danger btn-lg" onClick={this.closeNavigationSessions}>Close navigation sessions</button>
          </div>
          <UserNavigationSessions
            viewer={this.props.viewer}
            userId={user.id}
          />
        </div>
      )

    return (
      <table className="table table-hover table-condensed table-bordered">
        <tbody>
          <tr>
            <td>
              <Dialog ref={(el) => { this.dialog = el }} />
              {this._renderAvatarArea(user.avatar)}
              <TextField fieldName="Id" fieldContent={user.id}/>
              <TextField fieldName="Pseudo" fieldContent={user.pseudo}/>
              <TextField fieldName="Type" fieldContent={user.profileType}/>
              <TextField fieldName="FirstName" fieldContent={user.firstName}/>
              <TextField fieldName="LastName" fieldContent={user.lastName}/>
              <TextField fieldName="Sex" fieldContent={user.sex}/>
              <DateField fieldName="Birthday" fieldContent={user.birthday} />
              <TextField fieldName="Email" fieldContent={user.email}/>
              <TextField fieldName="Phone Prefix" fieldContent={user.phonePrefix}/>
              <TextField fieldName="Phone Number" fieldContent={user.phoneNumber}/>
              {user.mangoId !== null && (
                <TextField fieldName="Mangopay ID" fieldContent={user.mangoId}/>
              )}


              <div style={{display: 'flex',flexDirection:'row', paddingLeft:20, alignItems: 'center'}}>
                  <label>Can user make transfer to anyone</label>
                  <div style={{paddingLeft:20}} >
                    <input 
                      type="checkbox" 
                      className="custom-control-input" 
                      checked={user.isTrustedUserToMakeMoneyTransferToAnyone} 
                      onChange={event => this._handleTransferTrustedChange(event, user)}
                    />
                  </div>
              </div>

              <div style={user.isEmailValidated? styles.emailValidaded : styles.emailNotValidaded} >
                {user.isEmailValidated? 'email VALIDATED' : 'email not valided yet'}
              </div>
              {!user.isEmailValidated && (
                <div style={{ ...styles.emailValidaded, backgroundColor: 'transparent', marginLeft: 555 }}>
                  <button type="button" className="btn btn-success btn-sm" onClick={this._handleForceValidateUser}>Validate</button>
                </div>
              )}
            </td>
            <td>
                <TextField fieldName="Description" fieldContent={user.description}/>
                <TextField fieldName="Address" fieldContent={(user.address) ? (user.address.address+", "+user.address.city+", "+user.address.country) : "None"}/>
                <TextField fieldName="Reporters" fieldContent={user.reporters.length}/>
                <TextField fieldName="Sports" fieldContent={user.sports.length} />
                <TextField fieldName="Feedbacks" fieldContent={user.feedbacks.count}/>
                <TextField fieldName="Feedback Average rating" fieldContent={user.feedbacks.averageRating}/>
                <div style={{display: 'flex',flexDirection:'row', paddingLeft:20, alignItems: 'center'}}>
                  <label>Fees (%)</label>
                  <div style={{paddingLeft:20}} >
                    <input
                      ref="fees"
                      className="form-control"
                      placeholder="Fees"
                      defaultValue={user.fees}
                      onChange={(e) => this._handleFeesChange(e, user)}
                    />
                  </div>
                </div>
                <div style={{display: 'flex',flexDirection:'row', marginTop:20, paddingLeft:20, alignItems: 'center'}}>
                  <Tabs
                    groups={tabsSportunitiesOptions(this.props, this.state, this.setState.bind(this))}
                    id='tabsSportunities'
                  />
                </div>
                <div style={{display: 'flex',flexDirection:'row', marginTop:40, paddingLeft:20, alignItems: 'center'}}>
                  <Tabs
                    groups={tabsChatsOptions(this.props, this.state, this.setState.bind(this))}
                    id='tabsSportunities'
                  />
                </div>
            </td>
            <td>
                <DateField fieldName="Last connexion" fieldContent={user.lastConnexionDate}/>
                {this._renderStatusField(user)}
                <div style={{ marginTop: 20, marginRight: 10, }}>
                  <UserList
                    title="Authorized Managers"
                    edit={true}
                    users={user && user.authorized_managers.map((authorized) => authorized.user)}
                    viewer={this.props.viewer}
                    onSave={this._handleAuthorizedManagersSave}
                  />
                  <hr />
                </div>
                {this._getStatus(user) !== 'VALID' &&
                  <div style={{marginTop:20}} >
                    <label>Reason :</label>
                    <input
                      ref="reason"
                      className="form-control"
                      placeholder="Reason"
                      defaultValue={user.creation_status.reason}
                      onChange={(e) => this._handleReasonChange(e, user)}
                    />
                  </div>
                }
                <div style={{paddingLeft:20, marginTop:25}}>
                    <button type="button" className="btn btn-danger btn-lg" onClick={this.onSaveClick.bind(this)}>{this.props.text}</button>
                </div>
                <div style={{paddingLeft:20, marginTop:25}}>
                    <button type="button" className="btn btn-danger btn-lg" onClick={this.accessAsThisUser}>Login as the user</button>
                </div>
                <div style={{paddingLeft:20, marginTop:25}}>
                    <button type="button" className="btn btn-danger btn-lg" onClick={this.seeThisUserNavigationSessions}>See user navigation sessions</button>
                </div>
                <div style={{paddingLeft:20, marginTop:25}}>
                    <button type="button" className="btn btn-success btn-sm" onClick={this.onValidatePublicActivitiesClick.bind(this)}>Validate public activities</button>
                </div>
                <div style={{paddingLeft:20, marginTop:25}}>
                    <button type="button" className="btn btn-success btn-sm" onClick={this.onValidatePublicGroupsClick.bind(this)}>Validate public groups</button>
                </div>
            </td>
          </tr>
          <tr>
            <td>
                <h5>Reporters</h5>
                {user.reporters.map((reporter, index) => {
                    return (
                        <div key={index} className="panel panel-default">
                            <TextField fieldName={reporter.user.pseudo} fieldContent={reporter.reason}/>
                        </div>
                    )
                })}
            </td>
            <td>
                <h5>Feedbacks</h5>
                {(user.feedbacks.count > 0) ?
                    user.feedbacks.feedbacksList.edges.map((feedback, index) => {
                        return (
                            <div className="panel panel-default" key={index}>
                                <TextField fieldName={feedback.node.author.pseudo} fieldContent={'"'+feedback.node.text+'"'} />
                                <DateField fieldName="Created at" fieldContent={feedback.node.createdAt} />
                                <TextField fieldName="Rating" fieldContent={feedback.node.rating}/>
                            </div>
                        )
                    })
                    : ""
                }
            </td>
            <td>
                <h5>Sports</h5>
                {(user.sports.length > 0) ?
                    user.sports.map((sport, index) => {
                        return (
                          <div className="panel panel-default" key={index}>
                            <TextField fieldName="Name" fieldContent={sport.sport.name.EN}/>
                            <TextField fieldName="Positions" fieldContent={(sport.positions.length > 0) ? sport.positions.map((position,index) => { return (index > 0) ? (" - "+position.EN) : position.EN}) : "None"}/>
                            <TextField fieldName="Levels" fieldContent={(sport.levels.length > 0) ? sport.levels.map((level, index) => { return (index > 0) ? (" - "+level.EN.name) : level.EN.name}) : "None"}/>
                            {this._renderCertificatesField(sport.certificates, index)}
                          </div>
                        )
                    })
                    : ""
                }
            </td>
          </tr>
        </tbody>
      </table>
    )
  }
}

UserPanel.propTypes = ({
  user : React.PropTypes.object,
  onUpdateUser : React.PropTypes.func.isRequired,
  text : React.PropTypes.string.isRequired,
});

const emailValidationBase = {
  position: 'absolute',
  right: 'auto',
  top: 25,
  marginLeft: 415,
  fontWeight: 500,
  padding: 10,
  borderRadius: 5,
}

const styles = {
  emailValidaded: {
    ...emailValidationBase,
    backgroundColor: 'green',
    color: 'white',
  },
  emailNotValidaded: {
    ...emailValidationBase,
    backgroundColor: 'red',
    color: 'white',
  }
}

export default Relay.createContainer(UserPanel, {
  initialVariables: {
    filter: { status: '_' },
    fetchSportunities: false,
    userId: false,
    chatsSize: 100,
    fetchChats: false,
  },
  fragments: {
    viewer: () => Relay.QL`
      fragment on Viewer {
        sportunities(first: 20, filter: $filter, userId: $userId) @include (if: $fetchSportunities) {
          edges {
            node {
              id
              title
              description
              address {
                address
                city
                country
              }
              venue {
                name
                address {
                  address
                  city
                  country
                }
                owner {
                  pseudo
                }
              }
              price {
                cents
                currency
              }
              mode
              kind
              ageRestriction {
                from
                to
              }
              sexRestriction
              participantRange {
                from
                to
              }
              creation_status {
                status,
                reason
              }
              sport {
                levels {
                  EN {
                    name
                  }
                }
                allLevelSelected
                sport {
                  logo
                  name {
                    EN
                  }
                }
              }
              organizers {
                organizer {
                  pseudo
                }
                isAdmin
                role
              }
              beginning_date
              ending_date
              creation_date,
              cancel_date
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
        ${ValidateUserMutation.getFragment('viewer')}
        ${UpdateUserMutation.getFragment('viewer')}
        ${UserList.getFragment('viewer')}
        ${UserNavigationSessions.getFragment('viewer')}
      }
    `,
    user: () => Relay.QL`
      fragment on User {
        id,
        pseudo,
        firstName,
        lastName,
        email,
        isEmailValidated,
        isTrustedUserToCreateActivities,
        isTrustedUserToCreateCircles,
        avatar,
        description,
        phonePrefix,
        phoneNumber,
        birthday,
        sex,
        fees,
        lastConnexionDate,
        isTrustedUserToMakeMoneyTransferToAnyone,
        token
        profileType
        mangoId
        authorized_managers {
          authorization_level
          user {
            id
            pseudo
            avatar
          }
        }
        address {
          address,
          zip,
          city,
          country
        },
        sports {
          sport {
            id
            name {
              EN
            }
          }
          levels {
            id
            EN {
              name
            }
          }
          certificates {
            certificate {
              id
              name {
                EN
              }
            }
            validation
          }
          positions {
            id
            EN
          }
        }
        feedbacks {
          count,
          averageRating,
          feedbacksList (last:100) {
            edges {
              node {
                text,
                rating,
                author {
                  pseudo
                }
                createdAt
              }
            }
          }
        }
        fees
        reporters {
          user {
            pseudo
          }
          reason
        }
        creation_status {
          status,
          reason
        }

        chats(last:$chatsSize) @include(if: $fetchChats) {
          edges {
            node {
              id
              users {
                id
                pseudo
              }
              sportunity {
                id
                title
              }
              isActive
              messageCount
              messages(first:2) {
                edges {
                  node {
                    id
                    author {
                      id
                      pseudo
                    }
                    text
                    created
                  }
                }
              }
            }
          }

          pageInfo {
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `
  }
})



// WEBPACK FOOTER //
// ./src/components/UserPanel.js